'use client';

/**
 * SellerLandingCard — no-selection welcome dashboard for mode5.
 *
 * Replaces the legacy mode's passive `<EmptyCanvasWidget />` ("Select
 * an item from the explorer") with a real first-paint surface.
 * Shows: welcome heading + three at-a-glance summary tiles (open
 * RFQs, submitted responses, recent activity counts) + an explicit
 * "pick from the inbox" call-to-action.
 *
 * Data — reuses `vendor_portal_rfq_list` (the same strategy the
 * explorer card hits). React Query dedupes the fetch across the
 * two consumers, so the welcome surface is essentially free.
 *
 * @see klyde_edge/ui_config/composition/cards/seller.py
 *      content_key="seller_landing"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState } from 'react';
import {
  Inbox,
  CheckCircle2,
  MessageSquare,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useDevUserStore } from '@/lib/stores/dev-user-store';
import { useSellerLabel } from '@/lib/stores/total-matrix-store';
import { api } from '@/lib/api/client';
import { text, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { VPRfqSummary, VPRfqListResponse } from '@/lib/api/types';

interface Tally {
  open: number;
  submitted: number;
}

export function SellerLandingCard() {
  const selectedUser = useDevUserStore((s) => s.selectedUser);
  const listStrategy = useCapabilityStore((s) => s.strategyName('vendor_portal', 'list'));

  // Server-driven labels — Layer 1 chrome routes through seller_labels.
  const labelSubtitle = useSellerLabel('landing_subtitle');
  const labelTileOpen = useSellerLabel('landing_tile_open_rfqs');
  const labelTileSubmitted = useSellerLabel('landing_tile_submitted');
  const labelTileMessages = useSellerLabel('landing_tile_messages');
  const labelTileComingSoon = useSellerLabel('landing_tile_coming_soon');
  const labelCta = useSellerLabel('landing_cta');

  const [tally, setTally] = useState<Tally>({ open: 0, submitted: 0 });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!selectedUser || !listStrategy) return;
    let cancelled = false;
    setIsLoading(true);
    api
      .getStrategyData<VPRfqListResponse>(listStrategy, {})
      .then((res) => {
        if (cancelled || !res.success) return;
        const rfqs = res.data?.rfqs ?? [];
        const open = rfqs.filter((r: VPRfqSummary) => r.state === 'sent' || r.state === 'draft').length;
        const submitted = rfqs.filter((r: VPRfqSummary) => r.state === 'done').length;
        setTally({ open, submitted });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUser, listStrategy]);

  const sellerName = selectedUser?.name ?? 'there';
  const firstName = sellerName.split(' ')[0];

  return (
    <div style={{ fontSize: surfaceBase.spacious }} className="flex flex-col items-center justify-center h-full px-8">
      <div className="max-w-2xl w-full text-center">
        <WelcomeHeading firstName={firstName} />
        <p className={cn(text.lead, 'mb-12')}>{labelSubtitle}</p>

        <div className="grid grid-cols-3 gap-4 mb-10">
          <SummaryTile
            icon={Inbox}
            label={labelTileOpen}
            value={isLoading ? '—' : tally.open}
            tone="primary"
          />
          <SummaryTile
            icon={CheckCircle2}
            label={labelTileSubmitted}
            value={isLoading ? '—' : tally.submitted}
            tone="success"
          />
          <SummaryTile
            icon={MessageSquare}
            label={labelTileMessages}
            value="—"
            tone="muted"
            hint={labelTileComingSoon}
          />
        </div>

        <div className={cn(text.muted, 'flex items-center justify-center gap-2')}>
          <ArrowLeft className="h-4 w-4" />
          <span>{labelCta}</span>
          {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        </div>
      </div>
    </div>
  );
}

function WelcomeHeading({ firstName }: { firstName: string }) {
  const label = useSellerLabel('landing_welcome', { name: firstName });
  return <h1 className={cn(text.display, 'mb-2')}>{label}</h1>;
}

function SummaryTile({
  icon: Icon,
  label,
  value,
  tone,
  hint,
}: {
  icon: typeof Inbox;
  label: string;
  value: number | string;
  tone: 'primary' | 'success' | 'muted';
  hint?: string;
}) {
  const toneClass =
    tone === 'primary'
      ? 'bg-primary/5 text-primary border-primary/20'
      : tone === 'success'
        ? 'bg-green-500/5 text-green-600 dark:text-green-400 border-green-500/20'
        : 'bg-muted/30 text-muted-foreground border-border';
  return (
    <div className={`rounded-lg border p-4 ${toneClass}`}>
      <Icon className="h-5 w-5 mb-2 mx-auto opacity-80" />
      <div className="text-3xl font-semibold">{value}</div>
      <div className="text-xs uppercase tracking-wider mt-1 opacity-80">{label}</div>
      {hint && <div className="text-[10px] mt-1 opacity-60">{hint}</div>}
    </div>
  );
}
